import { createContext, Suspense, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { api, type Dashboard } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useResource, type Resource } from "../lib/useResource";
import { applyUpdate, useUpdateAvailable } from "../lib/update";
import { useAnchored } from "../lib/anchored";
import type { ChangelogEntry } from "../changelog";
import { ageSeconds, formatAge, formatReadingTakenAt } from "../lib/age";
import {
  activeTrail,
  FlyoutCaret,
  Icon,
  SECTIONS,
  sectionForPath,
  tabIsActive,
  type TabSpec,
} from "../lib/nav";
import { watchCells } from "../lib/stack";
import { recordVisit, labelForPath } from "../lib/backtrail";
import { watchTruncation } from "../lib/cells";
import SearchPalette from "./SearchPalette";
import { Loading } from "./States";
import Menu, { MenuFact, MenuSep } from "./Menu";
import Assistant from "./Assistant";
import FeedbackSnip from "./FeedbackSnip";
import OrbitWordmark from "./OrbitWordmark";
import AlertSymbol from "./AlertSymbol";
import ThemeToggle from "./ThemeToggle";
import logo from "../assets/vgh-logo-white.png";
import mark from "../assets/vgh-mark-white.png";

/**
 * The frame every signed-in page lives in.
 *
 * The shell owns one call to /api/dashboard and shares it. That is what makes
 * the coverage strip possible on every page without fifteen pages each making
 * their own round trip, and it is why the front page renders without a second
 * one.
 */

interface ShellData {
  dashboard: Resource<Dashboard>;
}
const ShellContext = createContext<ShellData | null>(null);

export function useShell(): ShellData {
  const context = useContext(ShellContext);
  if (!context) throw new Error("useShell used outside the shell.");
  return context;
}

/**
 * The last crumb of the breadcrumb: the record a detail page is showing.
 *
 * The shell knows the section and the page from the path; only the page knows
 * the NAME of the account or entity at /accounts/:id. A detail page calls
 * `useCrumb(name)` and the top bar reads "Accounts › Accounts › Vine Two".
 */
const CrumbContext = createContext<(leaf: string | null) => void>(() => {});

export function useCrumb(leaf: string | null | undefined) {
  const set = useContext(CrumbContext);
  useEffect(() => {
    set(leaf ?? null);
    // Cleared on unmount, not only on a route change: a drawer's detail
    // unmounts while the path stays put, and its name must leave with it.
    return () => set(null);
  }, [leaf, set]);
}

export default function Shell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const navigationType = useNavigationType();
  const dashboard = useResource(() => api.dashboard(), []);
  useSearchHotkey();
  const [leaf, setLeaf] = useState<string | null>(null);
  useEffect(() => setLeaf(null), [pathname]);
  // Remember each page as it is shown, so a detail page's "Back to <where>"
  // link can name the page you came from (lib/backtrail). The leaf name a
  // detail page sets lands a tick later, so this keys on the nav label, which is
  // enough to say "Back to Cash floors" / "Back to Accounts".
  useEffect(() => {
    recordVisit(pathname + location.search, labelForPath(pathname + location.search), navigationType);
  }, [pathname, location.search, navigationType]);

  // The section fly-out is JS-driven, so it has a GRACE period pure `:hover`
  // cannot give (Jonathan, 2026-10-21: hover-only dropped the panel the instant
  // the pointer strayed on the way from the row to it). Clicking a section
  // opens and sticks it; leaving keeps it open for a short beat so you can cross
  // the gap to the panel; and a quick graze over a neighbouring section waits
  // out an intent delay before switching. Paired with `.flyout-open` in
  // styles.css; `:focus-within` still drives it for the keyboard.
  const [openNav, setOpenNav] = useState<string | null>(null);
  // Pinned = opened by a CLICK, so it stays put regardless of the pointer
  // (Jonathan, 2026-10-21: "now that we have the grace I want it for clicking
  // also"). Hover-opened menus still close on the grace timer below; a pinned
  // one closes only on a re-click, a click outside, Escape or navigating.
  const [navPinned, setNavPinned] = useState(false);
  const navTimer = useRef<number | null>(null);
  const clearNavTimer = () => {
    if (navTimer.current !== null) {
      window.clearTimeout(navTimer.current);
      navTimer.current = null;
    }
  };
  const enterSec = (key: string) => {
    clearNavTimer();
    if (openNav === key) return; // already showing this one
    // Nothing open: show it now. A different one open: wait out a short intent
    // delay before switching, so grazing a neighbour does not snatch it. Either
    // way a hover drops any pin (the next section is hover-driven again).
    if (openNav === null) {
      setOpenNav(key);
      setNavPinned(false);
    } else {
      navTimer.current = window.setTimeout(() => {
        setOpenNav(key);
        setNavPinned(false);
      }, 140);
    }
  };
  const leaveSec = () => {
    clearNavTimer();
    if (navPinned) return; // a clicked-open menu stays until dismissed
    navTimer.current = window.setTimeout(() => setOpenNav(null), 280);
  };
  const clickSec = (key: string) => {
    clearNavTimer();
    if (openNav === key && navPinned) {
      setOpenNav(null);
      setNavPinned(false); // click the open section again to close it
    } else {
      setOpenNav(key);
      setNavPinned(true); // pin it open
    }
  };
  // Arriving on a page closes the fly-out; unmounting clears the timer.
  useEffect(() => {
    setOpenNav(null);
    setNavPinned(false);
  }, [pathname]);
  useEffect(() => () => clearNavTimer(), []);
  // A pinned menu is dismissed by a click anywhere outside the nav, or Escape.
  useEffect(() => {
    if (!navPinned) return undefined;
    const close = () => {
      setOpenNav(null);
      setNavPinned(false);
    };
    function onDown(event: MouseEvent) {
      if (!(event.target as HTMLElement).closest?.(".nav-group")) close();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [navPinned]);
  // The global search. The query lives here, not in the palette, so it is
  // still there the next time it opens (DESIGN.md §5: search remembers).
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  usePaletteHotkey(setSearchOpen);
  const { available: updateAvailable, whatsNew } = useUpdateAvailable();

  // Every table's cells learn their column header, so a phone can lay a row
  // out as a card with labels (lib/stack.ts). One observer over <main>.
  useEffect(() => {
    const main = document.getElementById("main");
    if (!main) return undefined;
    const stopLabels = watchCells(main);
    // ...and a truncated cell gets its full text on hover and opens on click.
    const stopTrunc = watchTruncation(main);
    return () => {
      stopLabels();
      stopTrunc();
    };
  }, []);

  const value = useMemo<ShellData>(() => ({ dashboard }), [dashboard]);

  const needsYou = dashboard.data?.needs_you.total ?? null;
  const held = dashboard.data?.needs_you.held_for_review ?? null;
  const current = sectionForPath(pathname);

  // A count on the section row, and -- while the section is open -- on the
  // page row that actually holds the work, so "10" on Banks is explained by
  // "Held for review 10" right under it.
  // Feedback nobody has picked up. Only the builder ever gets a non-zero
  // number here; for everyone else the tab is theirs to open.
  const waiting = dashboard.data?.feedback_waiting ?? null;
  const badges: Record<string, { n: number; alarm?: boolean } | null> = {
    // The red "needs you" count rides OPERATIONS, not Dashboard (Jonathan +
    // Sarah, 2026-10-25). The alerts moved off the front page to Operations in
    // the P90 redesign — "the Dashboard is a read-only statement now" — so the
    // one red mark on the nav has to point at the page that actually holds the
    // work, or it sends people to a page where there is nothing to act on.
    operations: needsYou ? { n: needsYou, alarm: true } : null,
    banks: held ? { n: held } : null,
    // Feedback folded into Settings on 2026-10-13, so its waiting count follows
    // it: on the desktop sidebar it lights the Settings row; on phone/tablet,
    // where Settings lives on the top bar, it lights the gear there.
    settings: waiting ? { n: waiting } : null,
  };
  const pageBadges: Record<string, number | null> = {
    "/connections/held": held,
    "/feedback": waiting,
  };

  // The phone/tablet bottom taskbar carries the six money sections; Settings
  // rides the top bar beside the account menu, so it is pulled out here.
  const settings = SECTIONS.find((s) => s.key === "settings") ?? null;
  const taskbarSections = SECTIONS.filter((s) => s.key !== "settings");

  const initials = (user?.full_name ?? "?")
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <ShellContext.Provider value={value}>
     <CrumbContext.Provider value={setLeaf}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      {/*
        The top bar. Only drawn under 900px -- phone and portrait tablet, where
        the sidebar has no room. The sidebar's sections ride the bottom taskbar
        now; this bar carries the brand on the left and, on the right, the two
        things that were in the sidebar's foot: Settings and the account menu.
        (Jonathan, 2026-10-13: "settings should go on the top bar next to the
        login logo".) No hamburger: there is no drawer to open any more.
      */}
      <header className="topbar">
        <img className="topbar-lockup" src={logo} alt="Vineyard Group Holdings" />
        {/*
          The update prompt, centred on the bar. The sidebar's foot -- where
          desktop shows the amber reload button and its "what's new" note -- is
          not on screen at a phone's width, so the prompt had been buried inside
          the account menu, where nobody saw it (Jonathan, 2026-10-13: "on
          mobile it does not show at all ... put it in the top header center").
          It is here only when a deploy has actually landed; the rest of the
          time the centre is empty. Absolutely centred so it sits on the bar's
          midline regardless of how wide the brand on the left and the controls
          on the right happen to be. The circled-i rides beside it, the same
          "what's new" the desktop foot carries -- that is the "info" the plain
          button cannot show on its own.
        */}
        {updateAvailable ? (
          <div className="topbar-center">
            <button
              className="update-available"
              onClick={applyUpdate}
              title="New version available. Reload; unsaved changes are lost."
            >
              <span className="glyph" aria-hidden="true">
                ↻
              </span>
              <span>Update</span>
            </button>
            {whatsNew.length > 0 ? <WhatsNew entries={whatsNew} /> : null}
          </div>
        ) : null}
        <div className="topbar-right">
          <CoverageChip onDark />
          <button
            type="button"
            className="topbar-search"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <SearchGlyph />
          </button>
          {/*
            Settings is a plain link to the Settings page, the same as the
            desktop sidebar row (Jonathan, 2026-10-27: "it should open up the
            settings page", not a fly-out). Its pages are listed on the page
            itself — `SettingsLayout`'s rail turns into a tab strip on a phone.
            The waiting-feedback count rides the icon, the same number that
            lights the Settings row on the desktop sidebar.
          */}
          {settings ? (
            <Link
              to={settings.to}
              className="topbar-search"
              aria-label="Settings"
              title="Settings"
            >
              <span className="topbar-gear" aria-hidden="true">
                <Icon name="gear" />
                {waiting ? <span className="nav-count">{waiting}</span> : null}
              </span>
            </Link>
          ) : null}
          <ThemeToggle onDark />
          {/*
            The account menu, the same one the desktop sidebar's foot carries.
            It no longer carries the update prompt: on a phone that prompt now
            sits centred on the top bar above (it was invisible buried in here),
            so like the desktop foot's copy this passes update={null}.
          */}
          <Menu
            label="Account"
            onDark
            trigger={
              <span className="avatar" aria-hidden="true">
                {initials}
              </span>
            }
          >
            <AccountMenuItems user={user} signOut={signOut} navigate={navigate} />
          </Menu>
        </div>
      </header>
      <div className="shell">
        <nav className="sidebar" id="sidebar" aria-label="Sections">
          {/*
            The real lockup, not a typographic stand-in.

            ⚠️ These PNGs are knocked out of `reference/VGH_Logo.jpeg`, which is
            a flattened charcoal mark on white -- there is no vector yet, and
            the v2 design spec carries that as an open item. It knocks out
            cleanly only because the mark is a true neutral, so luminance alone
            carries the shape and the antialiased edges survive as partial
            alpha. When the SVG or AI arrives, swap the files: nothing else
            here has to change.

            Two crops rather than one image scaled: at 60px the full lockup is
            unreadable, so the collapsed rail gets the monogram on its own.
          */}
          {/* The rail is navy in both themes, so the lockup and the Orbit wordmark are always their on-dark versions. */}
          <div className="sidebar-brand">
            <img
              className="lockup"
              src={logo}
              alt="Vineyard Group Holdings"
            />
            <img className="mark" src={mark} alt="Vineyard Group Holdings" />
          </div>

          {/*
            ⚠️ FLY-OUT NAV, not inline expand (P90, 2026-10-20). A section with
            pages opens them in a panel BESIDE its row on hover or keyboard
            focus — nothing unfolds downward, so the column never grows and the
            old height ceiling is gone. The open/close is pure CSS on `.sec`
            (`:hover`, `:focus-within`); see `.flyout` in styles.css. A tab with
            `children` (Reports) opens a second panel beside the first — the one
            level of nesting the direction asks for.
          */}
          <div className="nav-group">
            {SECTIONS.map((section) => {
              const badge = badges[section.key];
              const lit = current?.key === section.key;
              // A section with pages normally opens a fly-out; one marked
              // `flyout: false` (Settings) is a plain link whose pages live in an
              // in-page side rail instead (nav.tsx).
              const hasFlyout = section.tabs.length > 0 && section.flyout !== false;
              return (
                <div
                  key={section.key}
                  className={`sec ${section.key === "settings" ? "nav-settings" : ""} ${lit ? "active" : ""} ${hasFlyout ? "has-flyout" : ""} ${
                    hasFlyout && openNav === section.key ? "flyout-open" : ""
                  }`}
                  onMouseEnter={hasFlyout ? () => enterSec(section.key) : undefined}
                  onMouseLeave={hasFlyout ? leaveSec : undefined}
                >
                  {/*
                    ⚠️ A section WITH a fly-out is HOVER-ONLY (Jonathan,
                    2026-10-20): it is a non-navigating button, clicking does
                    nothing, and the panel is held open only by hover/keyboard
                    focus (`.sec:hover`/`.sec:focus-within` in styles.css) — it
                    never pins. Blur on click so a mouse click does not leave
                    the row focused (which would hold the panel open via
                    `:focus-within` until a click elsewhere). A section with NO
                    fly-out (Dashboard, Accounts, Banks) is a plain link that
                    navigates. */}
                  {hasFlyout ? (
                    <button
                      type="button"
                      title={section.label}
                      aria-haspopup="menu"
                      aria-expanded={openNav === section.key}
                      className={`nav-item ${lit ? "active" : ""}`}
                      onClick={() => clickSec(section.key)}
                    >
                      <Icon name={section.icon} />
                      <span className="grow">{section.label}</span>
                      {badge ? (
                        <span className={`nav-count ${badge.alarm ? "alarm" : ""}`}>
                          {badge.n}
                        </span>
                      ) : null}
                      <FlyoutCaret />
                    </button>
                  ) : (
                    <NavLink
                      to={section.to}
                      end={section.to === "/"}
                      title={section.label}
                      className={`nav-item ${lit ? "active" : ""}`}
                    >
                      <Icon name={section.icon} />
                      <span className="grow">{section.label}</span>
                      {badge ? (
                        <span className={`nav-count ${badge.alarm ? "alarm" : ""}`}>
                          {badge.n}
                        </span>
                      ) : null}
                    </NavLink>
                  )}
                  {hasFlyout ? (
                    <div
                      className="flyout"
                      role="menu"
                      aria-label={`${section.label} pages`}
                    >
                      <div className="flyout-title">{section.label}</div>
                      {section.tabs.map((tab) => (
                        <FlyoutItem
                          key={tab.to}
                          tab={tab}
                          pathname={pathname}
                          pageBadges={pageBadges}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="sidebar-foot">
            {/*
              A deploy has landed since this tab loaded. The button is the
              whole of it: it appears, it waits, and the page changes only
              when somebody presses it (Jonathan, 2026-09-17: "nothing happens
              until then"). See lib/update.ts for why it is never automatic.
              On the 60px rail the words fold away and the arrow remains.
            */}
            {updateAvailable ? (
              <div className="update-row">
                <button
                  className="update-available"
                  onClick={applyUpdate}
                  title="New version available. Reload; unsaved changes are lost."
                >
                  <span className="glyph" aria-hidden="true">
                    ↻
                  </span>
                  <span className="grow">Update available · Reload</span>
                </button>
                {/*
                  Beside the button, never inside it: the icon is its own
                  control, and a <button> nested in a <button> is invalid and
                  would fire the reload on the way to opening the popover.
                  `whatsNew` is never empty once an update is seen (a build with
                  no line of its own carries NO_NOTE), so this always shows.
                */}
                <WhatsNew entries={whatsNew} />
              </div>
            ) : null}
            {/*
              The credit is the wordmark itself, not the word, and it is a link
              to orbitcustom.com, in its own colours rather than the sidebar's
              (Jonathan, 2026-10-15: "ORBIT should be in the logo colors"). New tab: the
              people here are mid-task, and the site is not where they work.
            */}
            <a
              className="powered"
              href="https://orbitcustom.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Powered by Orbit — orbitcustom.com"
            >
              <span>Powered by</span>
              <OrbitWordmark tone="dark" />
            </a>
          </div>
        </nav>

        {/* <main>, not <div>. The skip link above targets this, and a skip
            link that lands on a div gives a screen reader somewhere to jump to
            without telling it what it jumped to -- the page then has no `main`
            landmark at all, which is the one every assistive technology looks
            for first. `tabIndex={-1}` stays: it is what makes the anchor
            actually move focus rather than only the viewport. */}
        <main className="main" id="main" tabIndex={-1}>
          {/*
            The desktop top bar (DESIGN.md §5), the same three slots on every
            page: where you are on the left, search in the middle, who you are
            on the right. Under 900px the phone top bar above does this job.
          */}
          <div className="deskbar no-print">
            <Breadcrumb pathname={pathname} leaf={leaf} />
            <button
              type="button"
              className="deskbar-search"
              onClick={() => setSearchOpen(true)}
              aria-label="Search (Ctrl+K)"
            >
              <SearchGlyph />
              <span className="grow">{searchQuery || "Search anything"}</span>
              <kbd>{IS_MAC ? "⌘K" : "Ctrl K"}</kbd>
            </button>
            <div className="deskbar-end">
              <CoverageChip />
              <ThemeToggle />
              <div className="deskbar-user">
                <span className="deskbar-who">
                  <span className="who">{user?.full_name}</span>
                  <span className="role">{user?.role}</span>
                </span>
                <Menu
                  label="Account"
                  trigger={
                    <span className="avatar" aria-hidden="true">
                      {initials}
                    </span>
                  }
                >
                  <AccountMenuItems user={user} signOut={signOut} navigate={navigate} />
                </Menu>
              </div>
            </div>
          </div>
          <PrintHeader />
          {/*
            Keyed by path so a new page mounts fresh and fades in rather than
            snapping (Jonathan, 2026-09-16: "when going from page to page, it
            jumps very fast"). The same fade is on every block a page mounts,
            so the skeleton and then the figures each arrive rather than
            appear -- `.page > *` in styles.css.
          */}
          <div className="page-enter" key={pathname}>
            {/* Each page is its own lazily-loaded chunk (App.tsx), so the first
                visit to one fetches its code. The boundary sits here, around the
                content only, so the sidebar and taskbar stay put and the wait
                reads exactly like a data load -- the same LogoLoader, in the same
                place -- rather than blanking the whole frame. */}
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      {/*
        The bottom taskbar -- phone and portrait tablet only (styles.css draws
        it under 900px and hides the sidebar). Six buttons: Dashboard, then the
        five money sections. Dashboard is a straight link; the rest open their
        pages in a sheet that rises from the bar (Jonathan, 2026-10-13: "when
        clicking on it the sub-items expand upwards").
      */}
      <Taskbar
        sections={taskbarSections}
        currentKey={current?.key ?? null}
        badges={badges}
        pageBadges={pageBadges}
      />
      {/* The assistant floats over every signed-in page. It hides itself when
          it is not armed, so it costs nothing where it is off. */}
      <Assistant />
      {/* Right-click anywhere → snip → file to Feedback. Costs nothing until a
          right-click; the capture library loads only when someone snips. */}
      <FeedbackSnip />
      {searchOpen ? (
        <SearchPalette
          query={searchQuery}
          onQuery={setSearchQuery}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
     </CrumbContext.Provider>
    </ShellContext.Provider>
  );
}

/**
 * One row in a section's fly-out. A leaf is a plain link; a tab with children
 * (Reports) is itself a `.sec` that opens a SECOND fly-out beside this one, on
 * hover or focus, with the same pure-CSS mechanism the section rows use.
 */
function FlyoutItem({
  tab,
  pathname,
  pageBadges,
}: {
  tab: TabSpec;
  pathname: string;
  pageBadges: Record<string, number | null>;
}) {
  const n = pageBadges[tab.to];
  const active = tabIsActive(tab, pathname);

  if (tab.children?.length) {
    // A tab that itself opens a sub-fly-out (Reports) is hover-only too: the
    // button does not navigate; its children (below) are the links. Blur on
    // click so it never pins.
    return (
      <div className={`sec sec-nested has-flyout ${active ? "active" : ""}`}>
        <button
          type="button"
          aria-haspopup="menu"
          className={`flyout-item ${active ? "active" : ""}`}
          onClick={(e) => e.currentTarget.blur()}
        >
          <span className="grow">{tab.label}</span>
          <FlyoutCaret />
        </button>
        <div className="flyout flyout-nested" role="menu" aria-label={tab.label}>
          {tab.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end={child.end}
              role="menuitem"
              className={`flyout-item ${tabIsActive(child, pathname) ? "active" : ""}`}
              onClick={(e) => e.currentTarget.blur()}
            >
              <span className="grow">{child.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    );
  }

  // A leaf navigates, then blurs so the panel closes on mouse-leave instead of
  // staying pinned open by the clicked link's focus (Jonathan, 2026-10-20).
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      role="menuitem"
      className={`flyout-item ${active ? "active" : ""}`}
      onClick={(e) => e.currentTarget.blur()}
    >
      <span className="grow">{tab.label}</span>
      {n ? <span className="nav-count dark">{n}</span> : null}
    </NavLink>
  );
}

/**
 * The account menu's choices, in one place because two menus carry them: the
 * desktop sidebar's foot and -- since the phone/tablet nav lost its drawer on
 * 2026-10-13 -- the top bar. Neither carries the "update available" prompt:
 * the desktop foot shows it as its own amber row just below this menu, and the
 * phone shows it centred on the top bar (2026-10-13). So both are the same list.
 */
function AccountMenuItems({
  user,
  signOut,
  navigate,
}: {
  user: ReturnType<typeof useAuth>["user"];
  signOut: ReturnType<typeof useAuth>["signOut"];
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <>
      <button onClick={() => navigate("/password")}>Change password</button>
      <MenuSep />
      <button
        onClick={() => {
          void signOut().then(() => navigate("/login", { replace: true }));
        }}
      >
        Sign out
      </button>
      {/* Which build this tab is running. Plumbing, so builder only -- and the
          fact that makes "Update available" checkable: press it and this
          changes. */}
      {user?.role === "builder" ? (
        <>
          <MenuSep />
          <MenuFact label="Build">{__BUILD_ID__}</MenuFact>
        </>
      ) : null}
    </>
  );
}

/**
 * The phone/tablet bottom bar.
 *
 * One row of six identical items, Dashboard on the left, all built the same way
 * (a link or a button, but the same markup and the same CSS path) so none can
 * drift a few pixels from the rest. It replaced the hamburger drawer on
 * 2026-10-13: a drawer hid every section's pages behind two taps and a slide,
 * and the sections people reach for all day were the ones buried.
 *
 * ⚠️ Tapping a section does NOT open a floating window. The navy bar itself
 * GROWS UPWARD (Jonathan, 2026-10-13: "it should open up full rows on top of
 * the tray with the same navy colour, like the tray expanding upwards, but the
 * icons stay put"). The pages are full-width rows stacked above the fixed icon
 * row, in the bar's own navy. One section open at a time; a backdrop, Escape,
 * an outside tap, or arriving on a page all close it.
 */
function Taskbar({
  sections,
  currentKey,
  badges,
  pageBadges,
}: {
  sections: typeof SECTIONS;
  currentKey: string | null;
  badges: Record<string, { n: number; alarm?: boolean } | null>;
  pageBadges: Record<string, number | null>;
}) {
  const { pathname } = useLocation();
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Arriving on a page closes the panel: tapping a row navigates, and the panel
  // should not be left standing over the page it just opened.
  useEffect(() => {
    setOpenKey(null);
  }, [pathname]);
  // Escape closes, to match every other overlay in the app.
  useEffect(() => {
    if (!openKey) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenKey(null);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openKey]);

  const openSection = sections.find((s) => s.key === openKey) ?? null;

  return (
    <>
      {/* A transparent catch for the tap that dismisses the panel. Below the
          bar, above everything else. */}
      {openSection ? (
        <div
          className="taskbar-backdrop"
          onClick={() => setOpenKey(null)}
          aria-hidden="true"
        />
      ) : null}
      <nav className="taskbar" aria-label="Sections">
        {/*
          The part that grows. Folded to 0fr and animated to 1fr, the same
          height trick the sidebar's sub-pages use, so the bar rises rather than
          snaps. Only the open section's rows are in here.
        */}
        <div className={`taskbar-panel ${openSection ? "open" : ""}`}>
          <div className="taskbar-panel-clip">
            {openSection ? (
              <div
                className="taskbar-rows"
                role="menu"
                aria-label={`${openSection.label} pages`}
              >
                {openSection.tabs.flatMap((tab) => {
                  const n = pageBadges[tab.to];
                  const row = (
                    <NavLink
                      key={tab.to}
                      to={tab.to}
                      end={tab.end}
                      role="menuitem"
                      className={`task-row ${
                        tabIsActive(tab, pathname) ? "active" : ""
                      }`}
                    >
                      <span className="grow">{tab.label}</span>
                      {n ? <span className="nav-count">{n}</span> : null}
                    </NavLink>
                  );
                  // A nested tab (Reports) has no inline fly-out on a phone, so
                  // its children follow it as indented rows — every page still
                  // reachable from the one open panel.
                  if (!tab.children?.length) return [row];
                  return [
                    row,
                    ...tab.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        role="menuitem"
                        className={`task-row task-sub ${
                          tabIsActive(child, pathname) ? "active" : ""
                        }`}
                      >
                        <span className="grow">{child.label}</span>
                      </NavLink>
                    )),
                  ];
                })}
              </div>
            ) : null}
          </div>
        </div>

        {/* The icon row, which never moves. */}
        <div className="taskbar-row">
          {sections.map((section) => {
            const lit = currentKey === section.key;
            const isOpen = openKey === section.key;
            const badge = badges[section.key];
            const inner = (
              <>
                <span className="task-ico">
                  <Icon name={section.icon} />
                  {badge ? (
                    <span
                      className={`nav-count ${badge.alarm ? "alarm" : ""}`}
                    >
                      {badge.n}
                    </span>
                  ) : null}
                </span>
                <span className="task-label">{section.label}</span>
              </>
            );

            // Dashboard has no pages -- a straight link. The rest toggle their
            // rows. Same class, so they look and size identically.
            return section.tabs.length < 2 ? (
              <NavLink
                key={section.key}
                to={section.to}
                end={section.to === "/"}
                className={`task-item ${lit ? "active" : ""}`}
                aria-label={section.label}
              >
                {inner}
              </NavLink>
            ) : (
              <button
                key={section.key}
                type="button"
                className={`task-item ${lit ? "active" : ""} ${
                  isOpen ? "open" : ""
                }`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={section.label}
                onClick={() =>
                  setOpenKey((k) => (k === section.key ? null : section.key))
                }
              >
                {inner}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}

const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/*
 * The "Back" control moved OUT of the header (Jonathan, 2026-10-25: "I don't
 * like it as a huge button in the main header"). It is a named link at the head
 * of a detail page's trail now — `components/PageBack` — "Back to Cash floors",
 * then the page's own contextual links. The history label it needs is recorded
 * per page by `recordVisit` above (lib/backtrail).
 */

function SearchGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.4 10.4 14 14" />
    </svg>
  );
}

/**
 * Where you are: Section › Page › the record, every crumb but the last a link.
 * A one-page section (Dashboard) is a single crumb. Built from the same
 * SECTIONS table as the sidebar, so the two can never disagree.
 */
export function Breadcrumb({ pathname, leaf }: { pathname: string; leaf: string | null }) {
  const section = sectionForPath(pathname);
  if (!section) return <nav className="crumbs" aria-label="Breadcrumb" />;
  const { tab, child } = activeTrail(section, pathname);
  const leafNode = child ? child.to : tab ? tab.to : section.to;
  const onLeaf = pathname === leafNode;
  const crumbs: { label: string; to: string | null }[] = [];
  if (tab) {
    if (tab.label !== section.label) {
      crumbs.push({ label: section.label, to: section.to });
    }
    // A parent tab (Reports) stays a link to its own landing even when a child
    // is open, since the child crumb below is the current page.
    crumbs.push({ label: tab.label, to: child ? tab.to : onLeaf ? null : tab.to });
    if (child) crumbs.push({ label: child.label, to: onLeaf ? null : child.to });
  } else {
    crumbs.push({ label: section.label, to: onLeaf ? null : section.to });
  }
  // A detail page under a leaf (/accounts/:id) adds the record's own name.
  if (!onLeaf && !child) crumbs.push({ label: leaf ?? "Detail", to: null });

  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <span key={i} className="crumb">
          {i > 0 ? <span className="crumb-sep" aria-hidden="true">›</span> : null}
          {c.to ? (
            <Link to={c.to}>{c.label}</Link>
          ) : (
            <span aria-current={i === crumbs.length - 1 ? "page" : undefined} title={c.label}>
              {c.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** ⌘K / Ctrl+K opens the global search from anywhere, even inside a field. */
function usePaletteHotkey(setOpen: (open: boolean) => void) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [setOpen]);
}

/**
 * "/" puts the cursor in this page's search box, wherever it is.
 *
 * One listener rather than one per page: a page opts in by giving its input
 * `id="search"`, and a page without one simply does nothing. Typing "/" inside
 * a field types a slash, which is what somebody entering a note expects.
 */
function useSearchHotkey() {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement;
      if (typing) return;

      const search = document.getElementById("search");
      if (search instanceof HTMLInputElement) {
        event.preventDefault();
        search.focus();
        search.select();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
}

/**
 * "What's new" — the lines this update brings that this tab had not seen.
 *
 * A circled "i" beside the reload button, opening a short list. It answers the
 * question the plain button cannot: a deploy landed, but WHAT changed? The
 * finance team does not read release notes, and a button that only says
 * "something is different" invites the suspicion that the screen moved under
 * them. A glance at three plain lines is the whole remedy.
 *
 * It borrows `useAnchored`, the same `position: fixed` panel the menus use, for
 * the same reason they do: the sidebar sets `overflow: hidden`, so a panel
 * anchored in flow here would be sliced off at the sidebar's edge. The hook
 * also carries outside-click, Escape (focus returns to the icon), and reflow.
 *
 * It is only ever rendered when there is something to say -- the caller hides
 * it on an empty list -- so there is no empty-state to draw here.
 */
function WhatsNew({ entries }: { entries: ChangelogEntry[] }) {
  const [open, setOpen] = useState(false);
  const { anchor, panel, style } = useAnchored<HTMLButtonElement>(
    open,
    () => setOpen(false),
    // Beside the icon, not above it: the button lives in the sidebar's bottom
    // corner, where "above" is the user's name line and the amber reload
    // button. See useAnchored's `align` doc for the full why.
    "side",
  );

  return (
    <div className="menu no-print">
      <button
        ref={anchor}
        type="button"
        className="whatsnew-info"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="What's new"
        onClick={() => setOpen((was) => !was)}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="8" cy="8" r="6.25" />
          <path d="M8 7.25v3.5" />
          <path d="M8 5.1v.05" strokeWidth="1.8" />
        </svg>
        {/* The label, not a bare icon. On a 216px sidebar the Reload button
            needs the whole row for its own words, so this sits UNDER it (see
            `.update-row`, now a column) rather than stealing width and
            truncating "Reload" to "Rel". A named control also answers Sarah's
            standing note that a silent glyph is unclear (2026-10-07). */}
        <span className="whatsnew-label">What&rsquo;s new</span>
      </button>

      {open ? (
        <div
          ref={panel}
          className="menu-pop whatsnew-pop"
          role="dialog"
          aria-label="What's new"
          style={style}
        >
          <div className="whatsnew-title">What's new</div>
          <ul className="whatsnew-list">
            {entries.length > 0 ? (
              entries.map((entry) => <li key={entry.id}>{entry.text}</li>)
            ) : (
              // A pure-plumbing deploy announces nothing, but the button still
              // appeared -- so answer the question the click asks ("what
              // changed?") plainly, rather than showing git jargon that reads
              // as alarming to someone who is not a developer.
              <li className="whatsnew-plumbing">
                Behind-the-scenes fixes &mdash; nothing you need to do.
              </li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What a printed page has to say about itself.
 *
 * Jacob asked for something he can put in front of people. A sheet of paper
 * with balances on it and no date is worse than no sheet of paper -- it is a
 * number with no way to tell whether it is from this morning or last March.
 *
 * ⚠️ IT KEEPS THE OLDEST READING, which the coverage strip dropped on
 * 2026-09-07. That is not an oversight and must not be "tidied" to match the
 * screen. On screen the age was removed because it read as staleness on a
 * current book and because the `PollerBand` says the same thing louder when it
 * is genuinely true. Paper has no band, no poller and no next page -- an
 * undated balance sheet that leaves this building is the exact failure this
 * header exists to prevent.
 */
function PrintHeader() {
  const { dashboard } = useShell();
  const board = dashboard.data;

  return (
    <div className="print-only print-header">
      <strong>VGH — back office</strong>
      <div>
        Printed {new Date().toLocaleString("en-US")}
        {board ? (
          <>
            {" · "}
            {board.coverage.accounts_linked} of {board.coverage.accounts_total}{" "}
            accounts connected
            {board.reach.oldest_reading_age !== null
              ? ` · oldest reading ${formatAge(
                  ageSeconds(
                    board.reach.oldest_reading_age,
                    dashboard.receivedAt ?? Date.now(),
                  ),
                )} at time of printing`
              : " · nothing read yet"}
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * "81/93 connected", on every page that still has something to report.
 *
 * Completeness is a condition, not a screen. A total seen without it invites a
 * decision on a partial picture, so you cannot be looking at a number in this
 * app without also being told how much of the book is behind it.
 *
 * ⚠️ **AND WHEN THERE IS NOTHING TO REPORT IT IS NOT HERE AT ALL, 2026-09-07.**
 * Jonathan: *"i don't need the header to always be there. as soon as all the
 * 93 accounts are connected i want the header removed."*
 *
 * That is not a retreat from the rule above, it is the rule finally applied.
 * The condition is "you cannot read a number without knowing how much of the
 * book is behind it" — and when the whole book is behind it, a strip saying so
 * on every page is a permanent banner reporting the absence of a problem. The
 * `PollerBand` next door makes exactly this argument about itself and has
 * since it was written: *a band that never goes quiet is a band people stop
 * reading.* This one never went quiet.
 *
 * ⚠️ **"CONNECTED" IS NOT "READ", AND HIDING ON COVERAGE ALONE WOULD HAVE
 * OPENED A HOLE.** `accounts_linked` counts accounts with a bank login
 * attached. An account can be linked and never read — that is most of the
 * book's first day, and it is also what a login stuck in `reauth_required`
 * looks like. Hiding at 93/93 LINKED would have taken the strip off screen
 * while half the accounts had no balance at all, which is precisely the
 * partial picture the strip exists to refuse.
 *
 * So it goes quiet when there is genuinely nothing to say: every account
 * linked, every account this viewer can see carrying a reading, and nothing
 * held for review. Any one of those failing brings it back on every page.
 *
 * WHAT IS NO LONGER HERE
 * ----------------------
 *   * **The oldest reading.** Jonathan: *"i don't think we need the oldest
 *     reading at all."* It had already lost its bold on 2026-09-04 and its
 *     place in the reading order on 2026-09-07, both times because it made a
 *     current book look like it was going off. A figure that has been demoted
 *     twice for being misread is a figure to remove, not to demote a third
 *     time. Staleness that actually matters is the `PollerBand`'s job, and it
 *     is louder than this ever was.
 *
 *   * **The auto-refresh note.** It moved next to `Refresh balances` on the
 *     Dashboard, which is the only place it was ever acting on: it exists to
 *     stop a billed click, and it now sits beside the button that spends the
 *     money rather than at the top of fifteen pages that cannot.
 *
 * Everything left is one font size, deliberately. The two sizes that were here
 * for three hours put `81/93` on a different baseline from the words beside it
 * — flex `align-items: center` centres each child's BOX, so two children with
 * different line heights get two different baselines and the line reads as
 * crooked.
 */
/**
 * Has the strip got anything to say.
 *
 * Pure, exported and tested, for the same reason `pollerHealth` and
 * `autoRefreshLead` are: this decides whether a warning appears on FIFTEEN
 * pages, and the states that matter are ones a browser is an expensive and
 * unreliable way to reach. Leaving it as a boolean expression inside JSX would
 * mean the only way to check "does a fully-read book hide the strip" is to
 * write a balance into a database and look.
 *
 * ⚠️ ALL THREE CLAUSES ARE LOAD-BEARING and none may be dropped to simplify:
 *
 *   * `linked` is what Jonathan asked for -- every account has a bank login.
 *   * `read` is what stops that becoming a hole. Linked is not read: an
 *     account can have a login and no balance, which is the first day of the
 *     book and also what a stuck `reauth_required` login looks like. Hiding on
 *     coverage alone takes the strip off screen while half the accounts show
 *     nothing.
 *   * `held` is a queue with somebody's decision waiting in it. A held item is
 *     not a completeness problem, but it IS the strip's other job, and going
 *     quiet on top of one would hide work rather than reassure.
 *
 * An EMPTY book is not a complete one. Both counts are guarded above zero, so
 * a viewer granted nothing, or a fresh install, keeps the strip -- "0/0
 * connected" is a state somebody needs to see, not a finished book.
 */
export function coverageWorthSaying(
  coverage: { accounts_total: number; accounts_linked: number; held_for_review: number },
  reach: { accounts_visible: number; accounts_with_reading: number },
): boolean {
  const everythingLinked =
    coverage.accounts_total > 0 &&
    coverage.accounts_linked === coverage.accounts_total;
  const everythingRead =
    reach.accounts_visible > 0 &&
    reach.accounts_with_reading === reach.accounts_visible;
  const nothingHeld = coverage.held_for_review === 0;

  return !(everythingLinked && everythingRead && nothingHeld);
}

/**
 * Coverage, shrunk to a chip for the top bar (P90, Jonathan 2026-10-20: "that
 * whole bar should become smaller and moved into the top bar ... just a small
 * thing, no huge banner").
 *
 * It replaces the full-width strip that sat under the deskbar — one of the two
 * stacked bars he objected to. The condition it reports is the same
 * (`coverageWorthSaying`), and it still goes away entirely once every account
 * is connected, read and nothing is held. The detail the strip spelled out
 * (what is unread, how many held) rides the `title` now; the page itself still
 * carries the figures. `onDark` is for the phone top bar, which is navy.
 */
function CoverageChip({ onDark = false }: { onDark?: boolean }) {
  const { dashboard } = useShell();
  const { user } = useAuth();

  // Quiet on the bar while loading or on error — a status chip that flickers
  // "Checking…" every page load is the noise this redesign is removing. The
  // page it sits above still shows the real state.
  if (dashboard.status !== "ready" || !dashboard.data) return null;

  const { coverage, reach } = dashboard.data;
  if (!coverageWorthSaying(coverage, reach)) return null;

  const held = coverage.held_for_review;
  const unread = Math.max(
    0,
    reach.accounts_visible - reach.accounts_with_reading,
  );

  // ⚠️ Clickable, with the full detail the old strip carried (Jonathan,
  // 2026-10-20: "when clicking on it there should be more information, the old
  // one had more"). The chip is the Menu's trigger (no circle, a plain pill
  // that matches the app's other pills); the detail — reload, what is unread,
  // what is held, when it was last read, what is visible — lives in the panel.
  return (
    <Menu
      label="Coverage detail"
      align="right"
      onDark={onDark}
      trigger={
        <span className={`cov-chip ${onDark ? "on-dark" : ""}`}>
          <span className="num">
            {coverage.accounts_linked}/{coverage.accounts_total}
          </span>
          <span className="cov-word">connected</span>
          {held > 0 ? (
            <AlertSymbol size={13} title={`${held} held for review`} />
          ) : null}
        </span>
      }
    >
      <button
        onClick={dashboard.reload}
        title="Re-reads stored figures. Does not ask the banks."
      >
        {dashboard.refreshing ? "Re-reading…" : "Reload stored figures"}
      </button>
      <MenuSep />
      <MenuFact label="Connected">
        {coverage.accounts_linked}/{coverage.accounts_total}
      </MenuFact>
      {reach.accounts_with_reading === 0 ? (
        <MenuFact label="Readings">nothing read yet</MenuFact>
      ) : unread > 0 ? (
        <MenuFact label="No balance">
          {unread} account{unread === 1 ? "" : "s"}
        </MenuFact>
      ) : null}
      {held > 0 ? <MenuFact label="Held">{held} for review</MenuFact> : null}
      {reach.accounts_with_reading > 0 ? (
        <MenuFact label="Last read">
          {formatReadingTakenAt(reach.newest_reading_age, dashboard.receivedAt)}
        </MenuFact>
      ) : null}
      <MenuFact label="Visible">
        {reach.entities_visible} entit{reach.entities_visible === 1 ? "y" : "ies"}{" "}
        · {reach.accounts_visible} account
        {reach.accounts_visible === 1 ? "" : "s"}
      </MenuFact>
      {user?.role === "builder" ? (
        <MenuFact label="Plaid items">
          {coverage.plaid_items_used}/{coverage.plaid_item_limit}
        </MenuFact>
      ) : null}
    </Menu>
  );
}
